import ActivitySector from "@/interfaces/ActivitySector";
import { IFilters, IPaginateResponse } from "@/interfaces/Fetch";
import Opco from "@/interfaces/Opco";
import { SingleValue } from "react-select";
import api from "../api";

export interface ActivitySectorForm {
  name: string;
  opco: SingleValue<Opco>;
}

export interface ActivitySectorFormSend
  extends Omit<ActivitySectorForm, "opco"> {
  name: string;
  opcoId: string;
}

export interface GetActivitySectorsParams extends IFilters {
  withMetadata?: boolean;
}

export const activitySectorsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getActivitySectors: build.query<
      IPaginateResponse<ActivitySector>,
      GetActivitySectorsParams | void
    >({
      query: (params) => ({
        url: "/activity-sectors",
        params: params ?? {},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "ActivitySectors" as const,
                id,
              })),
              { type: "ActivitySectors", id: "LIST" },
            ]
          : [{ type: "ActivitySectors", id: "LIST" }],
    }),
    getActivitySector: build.query<ActivitySector, string>({
      query: (id) => `/activity-sectors/${id}`,
      providesTags: (_result, _err, id) => [{ type: "ActivitySectors", id }],
    }),
    storeActivitySector: build.mutation<ActivitySector, ActivitySectorFormSend>(
      {
        query: (data) => ({
          url: "/activity-sectors",
          method: "POST",
          body: data,
        }),
        invalidatesTags: [{ type: "ActivitySectors", id: "LIST" }],
      }
    ),
    updateActivitySector: build.mutation<
      ActivitySector,
      [string, ActivitySectorFormSend]
    >({
      query: ([id, data]) => ({
        url: `/activity-sectors/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _err, [id]) => [
        { type: "ActivitySectors", id },
        { type: "ActivitySectors", id: "LIST" },
      ],
    }),
    destroyActivitySector: build.mutation<void, string>({
      query: (id) => ({
        url: `/activity-sectors/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "ActivitySectors", id: "LIST" }],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetActivitySectorsQuery,
  useLazyGetActivitySectorsQuery,
  useGetActivitySectorQuery,
  useStoreActivitySectorMutation,
  useUpdateActivitySectorMutation,
  useDestroyActivitySectorMutation,
} = activitySectorsApi;
